/**
 * AboutTab - About page CMS management
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Plus, Trash2, ChevronUp, ChevronDown, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AboutTab({ content, setContent }) {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const about = content.about || {};
  const sections = about.sections || [];

  const updateAbout = (field, value) => {
    setContent(prev => ({ ...prev, about: { ...prev.about, [field]: value } }));
  };

  const updateSection = (index, field, value) => {
    const updated = sections.map((s, i) => i === index ? { ...s, [field]: value } : s);
    updateAbout('sections', updated);
  };

  const addSection = () => {
    updateAbout('sections', [...sections, { title: '', subtitle: '', quote: '', quoteAuthor: '', image: '' }]);
  };

  const removeSection = (index) => {
    updateAbout('sections', sections.filter((_, i) => i !== index));
  };

  const moveSection = (from, to) => {
    if (to < 0 || to >= sections.length) return;
    const updated = [...sections];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    updateAbout('sections', updated);
  };

  const uploadImage = async (index, file) => {
    if (!file) return;
    setUploadingIndex(index);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', `about_section_${index}`);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/landing/upload-feature-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      updateSection(index, 'image', res.data.image_url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingIndex(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>About Page</CardTitle>
          <CardDescription>Page header and intro text</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Page Title</Label>
              <Input value={about.title || ''} onChange={(e) => updateAbout('title', e.target.value)} placeholder="e.g., About Us" data-testid="about-title" />
            </div>
            <div>
              <Label>Subheading</Label>
              <Input value={about.subtitle || ''} onChange={(e) => updateAbout('subtitle', e.target.value)} placeholder="e.g., Our story and mission" data-testid="about-subtitle" />
            </div>
          </div>
          <div>
            <Label>Body Text</Label>
            <Textarea value={about.body || ''} onChange={(e) => updateAbout('body', e.target.value)} placeholder="Introduction paragraph" rows={4} data-testid="about-body" />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Sections ({sections.length})</h3>
        <Button size="sm" onClick={addSection} className="bg-emerald-600 hover:bg-emerald-700" data-testid="about-add-section">
          <Plus className="w-4 h-4 mr-1" /> Add Section
        </Button>
      </div>

      {sections.map((section, index) => (
        <Card key={index}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Section {index + 1}</CardTitle>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveSection(index, index - 1)} disabled={index === 0}>
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveSection(index, index + 1)} disabled={index === sections.length - 1}>
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => removeSection(index)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input value={section.title || ''} onChange={(e) => updateSection(index, 'title', e.target.value)} data-testid={`about-section-title-${index}`} />
            </div>
            <div>
              <Label>Subheading</Label>
              <Textarea value={section.subtitle || ''} onChange={(e) => updateSection(index, 'subtitle', e.target.value)} rows={3} data-testid={`about-section-subtitle-${index}`} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Quote</Label>
                <Textarea value={section.quote || ''} onChange={(e) => updateSection(index, 'quote', e.target.value)} rows={2} placeholder="Optional quote" data-testid={`about-section-quote-${index}`} />
              </div>
              <div>
                <Label>Quote Author</Label>
                <Input value={section.quoteAuthor || ''} onChange={(e) => updateSection(index, 'quoteAuthor', e.target.value)} placeholder="Optional author name" data-testid={`about-section-author-${index}`} />
              </div>
            </div>
            <div>
              <Label>Image</Label>
              <div className="flex items-center gap-3 mt-1">
                {section.image && (
                  <img
                    src={section.image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${section.image}` : section.image}
                    alt="Section" className="w-20 h-14 object-cover rounded-lg border"
                  />
                )}
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(index, e.target.files[0])} />
                  <div className="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 transition-colors">
                    <Upload className="w-3.5 h-3.5" />
                    {uploadingIndex === index ? 'Uploading...' : section.image ? 'Replace' : 'Upload Image'}
                  </div>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
